import { motion } from "framer-motion";
import {
  webDev,
  whatIsTeensImg,
} from "../../../assets";
import Button from "../../../components/button/button";

const programs = [
  {
    number: "01",
    title: "Build",
    subtitle: "Web & Mobile",
    description: "Turn ideas into real digital products.",
    icon: "⌘",
  },
  {
    number: "02",
    title: "Explore",
    subtitle: "AI & Robotics",
    description: "Discover how intelligent technology works.",
    icon: "✦",
  },
  {
    number: "03",
    title: "Create",
    subtitle: "Design & Innovation",
    description: "Design, experiment and solve real problems.",
    icon: "◈",
  },
  {
    number: "04",
    title: "Collaborate",
    subtitle: "Team Challenges",
    description: "Build with other young innovators.",
    icon: "∞",
  },
];

const WhatsIsZTeens = () => {
  return (
    <section className="relative overflow-hidden bg-[#fafafa] py-16 sm:py-20 lg:py-28">

      {/* =====================================================
          AMBIENT BACKGROUND
      ===================================================== */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">

        <motion.div
          className="absolute -top-32 -left-32 w-[350px] h-[350px] rounded-full bg-[#59b5cd]/20 blur-[100px]"
          animate={{
            x: [0, 40, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <motion.div
          className="absolute top-[30%] right-[-120px] w-[400px] h-[400px] rounded-full bg-[#ecb32c]/20 blur-[120px]"
          animate={{
            x: [0, -50, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        <div className="absolute inset-0 opacity-[0.025] bg-[radial-gradient(#0f172a_1px,transparent_1px)] [background-size:22px_22px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* =====================================================
            TOP LABEL
        ===================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center mb-8"
        >
          <div className="inline-flex items-center gap-3 rounded-full border border-[#59b5cd]/20 bg-white/80 backdrop-blur-xl px-4 py-2 shadow-sm">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#ecb32c] opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ecb32c]" />
            </span>

            <span className="text-xs sm:text-sm font-bold tracking-[0.18em] uppercase text-[#0f172a]">
              Zulfah Academy presents
            </span>
          </div>
        </motion.div>

        {/* =====================================================
            HERO
        ===================================================== */}
        <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-10 lg:gap-20 items-center">

          {/* ================= TEXT ================= */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >

            {/* Age Badge */}
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="flex -space-x-2">
                {["8", "13", "18"].map((age, index) => (
                  <motion.div
                    key={age}
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      delay: index * 0.15,
                      type: "spring",
                    }}
                    className="w-9 h-9 rounded-full border-2 border-white bg-[#0f172a] text-white flex items-center justify-center text-xs font-bold"
                  >
                    {age}
                  </motion.div>
                ))}
              </div>

              <span className="text-sm font-semibold text-gray-500">
                Ages 8–18
              </span>
            </div>

            {/* Main Heading */}
            <h1 className="ruda text-4xl sm:text-5xl lg:text-[4.5rem] xl:text-[5.2rem] font-black leading-[0.95] tracking-[-0.04em] text-[#0f172a]">

              Don't just
              <br />

              <span className="relative inline-block">

                <span className="relative z-10 bg-gradient-to-r from-[#59b5cd] via-[#3fa9c0] to-[#ecb32c] bg-clip-text text-transparent">
                  use technology.
                </span>

                <motion.span
                  className="absolute left-0 bottom-1 w-full h-3 bg-[#ecb32c]/20 -z-0 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                />

              </span>

              <br />

              <span className="text-[#0f172a]">
                Build it.
              </span>
            </h1>

            {/* Description */}
            <p className="mt-7 max-w-xl mx-auto lg:mx-0 text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
              Zulfah is where young minds become{" "}
              <span className="font-bold text-[#0f172a]">
                creators, engineers and innovators.
              </span>{" "}
              Students learn by building real things from apps and websites
              to AI-powered ideas and robotics projects.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">

              <a
                href="https://docs.google.com/forms/d/e/1FAIpQLSfUuvrbrRAHm-fp1O0cJlnlSO21akZDgR7Kt3i1piufsdJi_g/viewform"
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  btnText="Join"
                  btnClassName="w-full sm:w-auto !px-8 !py-4 !bg-[#0f172a] hover:!bg-[#ecb32c] !text-white rounded-2xl shadow-xl hover:shadow-2xl hover:scale-[1.03] transition-all duration-300"
                />
              </a>

              <button
                onClick={() =>
                  document
                    .getElementById("zteen-programs")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="group px-7 py-4 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 font-bold text-[#0f172a] transition-all"
              >
                Explore Programs

                <span className="inline-block ml-2 transition-transform group-hover:translate-y-1">
                  ↓
                </span>
              </button>

            </div>

            {/* Mini Stats */}
            <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-x-8 gap-y-4">

              <div>
                <p className="text-2xl font-black text-[#0f172a]">8–18</p>
                <p className="text-xs uppercase tracking-wider text-gray-500">
                  Age Range
                </p>
              </div>

              <div className="w-px h-10 bg-gray-200 hidden sm:block" />

              <div>
                <p className="text-2xl font-black text-[#0f172a]">100%</p>
                <p className="text-xs uppercase tracking-wider text-gray-500">
                  Hands-On
                </p>
              </div>

              <div className="w-px h-10 bg-gray-200 hidden sm:block" />

              <div>
                <p className="text-2xl font-black text-[#0f172a]">∞</p>
                <p className="text-xs uppercase tracking-wider text-gray-500">
                  Possibilities
                </p>
              </div>

            </div>
          </motion.div>

          {/* ================= VISUAL ================= */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="relative"
          >

            {/* Main Image Frame */}
            <div className="relative max-w-[580px] mx-auto">

              {/* Rotating Ring */}
              <motion.div
                className="absolute -inset-5 rounded-[3rem] border border-dashed border-[#59b5cd]/30"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 30,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                whileHover={{ scale: 1.015 }}
                transition={{ duration: 0.4 }}
                className="relative rounded-[2rem] overflow-hidden bg-white border border-white shadow-[0_30px_80px_rgba(15,23,42,0.15)]"
              >

                <img
                  src={whatIsTeensImg}
                  alt="tech4Teens young technology innovators"
                  className="w-full aspect-[4/4.3] object-cover"
                />

                {/* Image Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/60 via-transparent to-transparent" />

                {/* Bottom Glass Card */}
                <div className="absolute bottom-5 left-5 right-5">

                  <div className="backdrop-blur-xl bg-white/15 border border-white/20 rounded-2xl p-4 sm:p-5 text-white">

                    <div className="flex items-center justify-between">

                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-white/70">
                          The tech4Teens Mission
                        </p>

                        <p className="mt-1 font-bold text-lg">
                          Learn. Build. Innovate.
                        </p>
                      </div>

                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                        }}
                        className="w-11 h-11 rounded-full bg-[#ecb32c] flex items-center justify-center text-[#0f172a] font-black text-xl"
                      >
                        ✦
                      </motion.div>

                    </div>

                  </div>

                </div>
              </motion.div>

              {/* Floating "CREATOR" CARD */}
              <motion.div
                animate={{
                  y: [0, -10, 0],
                  rotate: [0, 2, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -left-4 sm:-left-8 top-12 bg-white rounded-2xl shadow-xl border border-gray-100 px-4 py-3 flex items-center gap-3"
              >

                <div className="w-10 h-10 rounded-xl bg-[#59b5cd]/10 flex items-center justify-center text-xl">
                  💡
                </div>

                <div>
                  <p className="text-xs text-gray-400">Future</p>
                  <p className="font-black text-[#0f172a]">Creator</p>
                </div>

              </motion.div>

              {/* Floating CODE CARD */}
              <motion.div
                animate={{
                  y: [0, 12, 0],
                  rotate: [0, -2, 0],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -right-4 sm:-right-8 bottom-20 bg-[#0f172a] text-white rounded-2xl shadow-xl px-4 py-3"
              >
                <div className="font-mono text-sm">
                  <span className="text-[#59b5cd]">&lt;</span>
                  build
                  <span className="text-[#ecb32c]"> /&gt;</span>
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  Something amazing
                </p>
              </motion.div>

            </div>
          </motion.div>

        </div>

        {/* =====================================================
            PROGRAMS
        ===================================================== */}
        <div
          id="zteen-programs"
          className="mt-24 sm:mt-32"
        >

          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-10"
          >

            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#59b5cd]">
              What happens at tech4Teens?
            </p>

            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-black text-[#0f172a] ruda">
              Curiosity becomes
              <span className="text-[#ecb32c]"> capability.</span>
            </h2>

          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

            {programs.map((program, index) => (

              <motion.div
                key={program.number}
                initial={{ opacity: 0, y: 35 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.5,
                  delay: index * 0.1,
                }}
                whileHover={{ y: -8 }}
                className="group relative overflow-hidden rounded-3xl bg-white border border-gray-100 p-6 shadow-sm hover:shadow-2xl transition-shadow duration-500"
              >

                {/* Number */}
                <div className="flex items-center justify-between">

                  <span className="text-xs font-black tracking-widest text-gray-300">
                    {program.number}
                  </span>

                  <div className="w-11 h-11 rounded-xl bg-gray-50 group-hover:bg-[#0f172a] flex items-center justify-center text-lg group-hover:text-white transition-colors duration-300">
                    {program.icon}
                  </div>

                </div>

                <h3 className="mt-8 text-2xl font-black text-[#0f172a]">
                  {program.title}
                </h3>

                <p className="mt-1 font-bold text-[#59b5cd]">
                  {program.subtitle}
                </p>

                <p className="mt-4 text-sm leading-relaxed text-gray-500">
                  {program.description}
                </p>

                {/* Hover Arrow */}
                <div className="mt-7 flex items-center justify-between">

                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Discover
                  </span>

                  <span className="w-8 h-8 rounded-full bg-gray-50 group-hover:bg-[#ecb32c] flex items-center justify-center transition-colors">
                    →
                  </span>

                </div>

              </motion.div>

            ))}

          </div>
        </div>

        {/* =====================================================
            FINAL CTA
        ===================================================== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative mt-20 sm:mt-28 overflow-hidden rounded-[2rem] bg-[#0f172a] p-8 sm:p-12 lg:p-16 text-center"
        >

          {/* Decorative circles */}
          <div className="absolute -top-24 -left-24 w-64 h-64 rounded-full bg-[#59b5cd]/20 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-[#ecb32c]/20 blur-3xl" />

          <div className="relative z-10">

            <div className="mx-auto w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-[#ecb32c] text-2xl">
              ✦
            </div>

            <h2 className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-black text-white ruda">
              The future isn't waiting.
              <br />
              <span className="text-[#ecb32c]">
                Start building it.
              </span>
            </h2>

            <p className="mt-5 max-w-2xl mx-auto text-gray-300 text-sm sm:text-base lg:text-lg">
              Give your child more than screen time. Give them the skills,
              confidence and environment to create what comes next.
            </p>

            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLSfUuvrbrRAHm-fp1O0cJlnlSO21akZDgR7Kt3i1piufsdJi_g/viewform"
              target="_blank"
              rel="noreferrer"
              className="inline-block mt-8"
            >
              <Button
                btnText="Start →"
                btnClassName="!px-9 !py-4 !bg-[#ecb32c] hover:!bg-white !text-[#0f172a] rounded-2xl font-black shadow-xl hover:scale-105 transition-all duration-300"
              />
            </a>

          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default WhatsIsZTeens;